/**
 * ContactPinPanel
 *
 * Painel lateral que abre quando o usuário clica em um pin no mapa.
 * Exibe informações básicas do contato e link para o perfil completo.
 */

import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Mail, MapPin, Tag, Star, ExternalLink, Loader2 } from 'lucide-react';
import { useContactMapDetail } from '../hooks/useGeoData';
import { useContactCategories } from '@/features/crm/hooks/useContacts';

interface ContactPinPanelProps {
  contactId: string | null;
  onClose:   () => void;
}

export function ContactPinPanel({ contactId, onClose }: ContactPinPanelProps) {
  const navigate  = useNavigate();
  const { data: contact, isLoading } = useContactMapDetail(contactId);
  const { data: categories = [] } = useContactCategories();

  const categoryLabel = contact
    ? (categories.find((c) => c.slug === contact.category)?.label ?? contact.category)
    : '';

  const categoryColor = contact
    ? (categories.find((c) => c.slug === contact.category)?.color_class ?? 'bg-slate-500/15 text-slate-300 border-slate-500/25')
    : '';

  if (!contactId) return null;

  return createPortal(
    <>
      {/* Overlay mobile */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
        onClick={onClose}
      />

      {/* Painel */}
      <div className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[360px] flex flex-col card-premium border-l border-hairline shadow-2xl animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-hairline shrink-0">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-accent" />
            <span className="text-sm font-semibold text-ink">Contato selecionado</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-muted hover:text-ink hover:bg-white/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-accent" />
            </div>
          ) : !contact ? (
            <p className="text-sm text-ink-muted text-center py-8">Contato não encontrado.</p>
          ) : (
            <>
              {/* Avatar + nome */}
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 shrink-0 rounded-2xl bg-accent/15 border border-accent/25 flex items-center justify-center">
                  <span className="text-xl font-bold text-accent">
                    {contact.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg font-bold text-ink leading-tight">{contact.full_name}</p>
                  {categoryLabel && (
                    <span className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${categoryColor}`}>
                      {categoryLabel}
                    </span>
                  )}
                </div>
              </div>

              {/* Engajamento */}
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-400 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-ink-muted">Engajamento</span>
                    <span className="text-xs font-bold text-ink">{contact.engagement_score}</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-amber-400 transition-all"
                      style={{ width: `${Math.min(contact.engagement_score, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div className="space-y-2">
                {contact.phone && (
                  <a
                    href={`tel:${contact.phone}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-midnight-2/60 border border-hairline hover:border-accent/30 transition-colors group"
                  >
                    <Phone className="h-4 w-4 text-ink-muted group-hover:text-accent shrink-0" />
                    <span className="text-sm text-ink">{contact.phone}</span>
                  </a>
                )}
                {contact.email && (
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-midnight-2/60 border border-hairline hover:border-accent/30 transition-colors group"
                  >
                    <Mail className="h-4 w-4 text-ink-muted group-hover:text-accent shrink-0" />
                    <span className="text-sm text-ink truncate">{contact.email}</span>
                  </a>
                )}
              </div>

              {/* Localização */}
              {(contact.neighborhood || contact.city) && (
                <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-midnight-2/60 border border-hairline">
                  <MapPin className="h-4 w-4 text-ink-muted shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    {contact.neighborhood && (
                      <p className="text-sm text-ink">{contact.neighborhood}</p>
                    )}
                    {contact.city && (
                      <p className="text-xs text-ink-muted">
                        {contact.city}{contact.state_code ? ` — ${contact.state_code}` : ''}
                      </p>
                    )}
                    {contact.latitude && contact.longitude && (
                      <p className="text-[10px] text-ink-subtle font-mono mt-0.5">
                        {contact.latitude.toFixed(5)}, {contact.longitude.toFixed(5)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {contact.tags.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-ink-subtle" />
                    <span className="text-xs font-semibold text-ink-muted uppercase tracking-wider font-mono">Tags</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {contact.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-full bg-white/5 border border-hairline px-2 py-0.5 text-[11px] text-ink-muted"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notas */}
              {contact.notes && (
                <div className="rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2.5">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-ink-subtle mb-1">Obs.</p>
                  <p className="text-sm text-ink-muted leading-relaxed">{contact.notes}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {contact && (
          <div className="px-5 py-4 border-t border-hairline shrink-0">
            <button
              onClick={() => navigate(`/app/contatos/${contact.id}`)}
              className="flex items-center justify-center gap-2 w-full btn-accent rounded-xl px-4 py-2.5 text-sm font-semibold"
            >
              <ExternalLink className="h-4 w-4" />
              Ver perfil completo
            </button>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
